import { defineStore } from "pinia";
import { useGlobalStore } from "@stores/global";
import { LAUNCH_AT, LAUNCH_DAY_END } from "@utils/launch";

const shouldTrack = true;

const USER_COOKIE = "user";
const PROGRESS_COOKIE = "achievements_progress";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const LAST_VISIT_KEY = "exspeedia_last_visit";
const MIN_GAP_MS = 20 * 60 * 60 * 1000;
const MAX_GAP_MS = 48 * 60 * 60 * 1000;

function trackingActive() {
    return shouldTrack && Date.now() >= LAUNCH_AT;
}

function getCookie(name) {
    if (typeof document === "undefined") return null;
    const match = document.cookie
        .split("; ")
        .find((row) => row.startsWith(name + "="));
    if (!match) return null;
    try {
        return JSON.parse(decodeURIComponent(match.split("=")[1]));
    } catch {
        return null;
    }
}

function setCookie(name, value) {
    if (typeof document === "undefined") return;
    const encoded = encodeURIComponent(JSON.stringify(value));
    document.cookie = `${name}=${encoded}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

function deleteCookie(name) {
    if (typeof document === "undefined") return;
    document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
}

function loadUser() {
    if (!trackingActive()) return { achievements: [] };
    const existing = getCookie(USER_COOKIE);
    if (!existing || typeof existing !== "object") return { achievements: [] };
    if (!Array.isArray(existing.achievements)) existing.achievements = [];
    return existing;
}

function loadProgress() {
    if (!trackingActive()) return {};
    return getCookie(PROGRESS_COOKIE) ?? {};
}

export const useAchievementsStore = defineStore("achievements", {
    state: () => ({
        definitions: [],
        loaded: false,
        user: loadUser(),
        progress: loadProgress(),
        toastQueue: [],
        pendingVisits: [],
    }),

    getters: {
        byId: (state) => (id) =>
            state.definitions.find((d) => d.id === id) ?? null,

        bySlug: (state) => (slug) =>
            state.definitions.find((d) => d.slug === slug) ?? null,

        rawCount(state) {
            return (id) => {
                const entry = state.user.achievements.find((a) => a[0] === id);
                return entry ? entry[1] : 0;
            };
        },

        isComplete() {
            return (slug) => {
                const def = this.bySlug(slug);
                if (!def) return false;
                const count = this.rawCount(def.id);
                const last = def.levels[def.levels.length - 1];
                return count >= last.target_end;
            };
        },

        currentProgress() {
            return (slug) => {
                const def = this.bySlug(slug);
                if (!def) return null;

                const count = this.rawCount(def.id);
                const lastIdx = def.levels.length - 1;
                const lastLevel = def.levels[lastIdx];
                const complete = count >= lastLevel.target_end;

                const levelIndex = complete
                    ? lastIdx
                    : def.levels.findIndex((l) => count < l.target_end);
                const level = def.levels[levelIndex];

                const span = Math.max(1, level.target_end - level.target_start);
                const visited = Math.min(
                    span,
                    Math.max(0, count - level.target_start),
                );
                const percent = complete
                    ? 100
                    : Math.min(100, Math.round((visited / span) * 100));

                return {
                    visited,
                    total: level.target_end - level.target_start,
                    percent,
                    level,
                    levelIndex,
                    totalLevels: def.levels.length,
                    isComplete: complete,
                    rawCount: count,
                };
            };
        },
    },

    actions: {
        async setup() {
            if (!shouldTrack) return;
            await this.fetch();
        },

        // Called from globalStore.startPlaying(). If the user clicked Start now
        // after launch but state was init'd pre-launch with empty cookies, we
        // reload here so any existing cookie progress is picked up.
        onEnterApp() {
            if (!trackingActive()) return;
            if (this.user.achievements.length === 0) {
                this.user = loadUser();
                this.progress = loadProgress();
                this.seedProgress();
            }
            this.checkEarlyBird();
            this.checkDailyReturn();
        },

        async fetch() {
            try {
                const { data } = await axios.get("/api/achievements");
                this.definitions = data;
                this.loaded = true;
                this.seedProgress();
                this._drainPendingVisits();
            } catch (error) {
                console.error(error);
            }
        },

        seedProgress() {
            let changed = false;
            for (const def of this.definitions) {
                if (!this.progress[def.id]) {
                    const count = this.rawCount(def.id);
                    const last = def.levels[def.levels.length - 1];
                    this.progress[def.id] = {
                        id: def.id,
                        name: def.name,
                        progress: count,
                        completed: count >= last.target_end,
                    };
                    changed = true;
                }
            }
            if (changed) this._persistProgress();
        },

        incrementProgress(slug, by = 1) {
            if (!trackingActive()) return;
            const def = this.bySlug(slug);
            if (!def) return;
            const oldCount = this.rawCount(def.id);
            this._applyCount(def, oldCount, oldCount + by);
        },

        setProgress(slug, value) {
            if (!trackingActive()) return;
            const def = this.bySlug(slug);
            if (!def) return;
            const oldCount = this.rawCount(def.id);
            this._applyCount(def, oldCount, value);
        },

        visit(slug, itemId) {
            if (!trackingActive()) return;
            if (itemId == null) return;
            if (!this.loaded) {
                this.pendingVisits.push({ slug, itemId });
                return;
            }
            const def = this.bySlug(slug);
            if (!def) return;

            const current = this.progress[def.id] ?? {};
            const items = Array.isArray(current.items) ? current.items : [];
            if (items.includes(itemId)) return;

            const nextItems = [...items, itemId];
            const oldCount = this.rawCount(def.id);
            this._writeProgress(def, oldCount, nextItems.length, nextItems);
        },

        _applyCount(def, oldCount, newCount) {
            this._writeProgress(def, oldCount, newCount);
        },

        _writeProgress(def, oldCount, newCount, items) {
            const lastLevel = def.levels[def.levels.length - 1];
            const clamped = Math.max(
                0,
                Math.min(newCount, lastLevel.target_end),
            );

            this._upsertUser(def.id, clamped);

            const prev = this.progress[def.id] ?? {};
            const nextItems = items ?? prev.items;
            this.progress[def.id] = {
                id: def.id,
                name: def.name,
                progress: clamped,
                completed: clamped >= lastLevel.target_end,
                ...(Array.isArray(nextItems) ? { items: nextItems } : {}),
            };

            this._persistUser();
            this._persistProgress();

            const crossed = [...def.levels]
                .reverse()
                .find(
                    (l) => oldCount < l.target_end && clamped >= l.target_end,
                );
            if (crossed) {
                this._enqueueToast({
                    title: def.name,
                    description: crossed.name,
                    icon: def.icon,
                });

                if (typeof window.gtag === "function") {
                    window.gtag("event", "unlocked_achievement", {
                        achievement_name: def.name,
                        achievement_level: crossed.name,
                    });
                }

                const finalTier = def.levels[def.levels.length - 1];
                const isTieredAchievement = def.levels.length > 1;
                if (
                    crossed === finalTier &&
                    isTieredAchievement &&
                    def.slug !== "completionist"
                ) {
                    this.incrementProgress("completionist", 1);
                }
            }
        },

        _enqueueToast(payload) {
            this.toastQueue.push(payload);
            this._drainToasts();
        },

        _drainToasts() {
            const globalStore = useGlobalStore();
            if (globalStore.achievement.active) return;
            const next = this.toastQueue.shift();
            if (!next) return;
            globalStore.showAchievement(next);
        },

        advanceToastQueue() {
            setTimeout(() => this._drainToasts(), 300);
        },

        _drainPendingVisits() {
            const pending = this.pendingVisits.splice(0);
for (const { slug, itemId } of pending) {
                this.visit(slug, itemId);
            }
        },

        _upsertUser(id, count) {
            const idx = this.user.achievements.findIndex((a) => a[0] === id);
            if (idx === -1) this.user.achievements.push([id, count]);
            else this.user.achievements[idx] = [id, count];
        },

        checkEarlyBird() {
            if (Date.now() > LAUNCH_DAY_END) return;
            const def = this.bySlug("early-bird");
            if (!def) return;
            if (this.rawCount(def.id) >= 1) return;
            this.incrementProgress("early-bird", 1);
        },

        checkDailyReturn() {
            const now = Date.now();
            const raw = localStorage.getItem(LAST_VISIT_KEY);
            const last = raw ? Date.parse(raw) : NaN;

            if (Number.isNaN(last)) {
                localStorage.setItem(
                    LAST_VISIT_KEY,
                    new Date(now).toISOString(),
                );
                return;
            }

            const gap = now - last;
            if (gap < MIN_GAP_MS) return;

            if (gap <= MAX_GAP_MS) {
                this.incrementProgress("daily-ritual", 1);
            } else {
                this.setProgress("daily-ritual", 0);
            }
            localStorage.setItem(LAST_VISIT_KEY, new Date(now).toISOString());
        },

        _persistUser() {
            if (!trackingActive()) return;
            setCookie(USER_COOKIE, this.user);
        },

        _persistProgress() {
            if (!trackingActive()) return;
            setCookie(PROGRESS_COOKIE, this.progress);
        },
    },
});
